import { signInWithCustomToken } from 'firebase/auth';
import { auth } from './firebase';
import CryptoService from './cryptoService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface ClientSideValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface AuthResponse {
  success: boolean;
  firebaseToken?: string;
  user: {
    id: string;
    walletAddress: string;
    username: string;
    displayName: string;
    bio: string;
    profilePicture: string;
    bannerImage: string;
    level: number;
    xp: number;
    badges: any[];
    socialLinks: {
      twitter: string;
      telegram: string;
      website: string;
      discord: string;
    };
    isVerified: boolean;
    role: 'user' | 'moderator' | 'admin';
    following: string[];
    followers: string[];
    followersCount: number;
    followingCount: number;
    createdAt: Date;
    updatedAt?: Date;
  };
}

export interface ChallengeResponse {
  message: string;
  nonce: number;
  timestamp: number;
  walletAddress: string;
}

export class BackendAuthService {
  // Obtenir un message de défi pour la signature
  static async getChallenge(walletAddress: string): Promise<ChallengeResponse> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/challenge/${walletAddress}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération du défi');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur getChallenge:', error);
      throw error;
    }
  }

  // Vérifier la signature (version simplifiée pour le développement)
  static async verifySignature(authData: {
    walletAddress: string;
    signature: string;
    message: string;
    timestamp: number;
    userData?: {
      username?: string;
      displayName?: string;
      bio?: string;
      avatar?: string;
    };
  }): Promise<AuthResponse> {
    try {
      // Hash des données sensibles avant envoi
      const hashedAuthData = {
        ...authData,
        // Hash de l'adresse wallet pour sécuriser la transmission
        walletAddressHash: CryptoService.hashString(authData.walletAddress),
        // Anonymise l'adresse pour les logs
        walletAddressAnonymized: CryptoService.anonymizeWallet(authData.walletAddress),
        userData: authData.userData ? CryptoService.hashUserSensitiveData(authData.userData) : undefined
      };

      console.log('🔐 Envoi des données hashées:', {
        walletAddressAnonymized: hashedAuthData.walletAddressAnonymized,
        hasHash: !!hashedAuthData.walletAddressHash
      });

      const response = await fetch(`${BACKEND_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress: authData.walletAddress, // Garde l'original pour la vérification de signature
          signature: authData.signature,
          message: authData.message,
          timestamp: authData.timestamp,
          userData: hashedAuthData.userData, // Utilise les données hashées
          walletAddressHash: hashedAuthData.walletAddressHash
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la vérification');
      }

      const data = await response.json();
      
      // Retourner directement la réponse du backend
      return data;
    } catch (error) {
      console.error('Erreur verifySignature:', error);
      throw error;
    }
  }

  // Se connecter avec le backend simplifié (sans Firebase pour le moment)
  static async signInWithBackend(walletAddress: string, signMessage: (message: Uint8Array) => Promise<Uint8Array>): Promise<AuthResponse> {
    try {
      console.log('🔐 Début de l\'authentification pour:', walletAddress);
      
      // 1. Obtenir le défi
      console.log('📝 Récupération du défi...');
      const challenge = await this.getChallenge(walletAddress);
      console.log('✅ Défi reçu:', challenge);
      
      // 2. Signer le message
      console.log('✍️ Signature du message...');
      const messageBytes = new TextEncoder().encode(challenge.message);
      const signatureBytes = await signMessage(messageBytes);
      console.log('✅ Message signé, taille signature:', signatureBytes.length);
      
      // 3. Convertir la signature en base58 et valider
      const bs58 = await import('bs58');
      const signature = bs58.default.encode(signatureBytes);
      console.log('✅ Signature en base58:', signature.slice(0, 20) + '...');
      
      // 3.5. Validation côté client avant envoi
      console.log('🔍 Validation signature côté client...');
      const validationResult = this.validateSignatureFormat(signature, signatureBytes.length, walletAddress);
      if (!validationResult.isValid) {
        throw new Error(`Signature invalide côté client: ${validationResult.errors.join(', ')}`);
      }
      if (validationResult.warnings.length > 0) {
        console.warn('⚠️ Avertissements signature:', validationResult.warnings);
      }
      console.log('✅ Signature validée côté client');
      
      // 4. Vérifier la signature avec le backend
      console.log('🔍 Vérification avec le backend...');
      const authResponse = await this.verifySignature({
        walletAddress,
        signature,
        message: challenge.message,
        timestamp: challenge.timestamp,
      });
      console.log('✅ Réponse backend:', authResponse);

      // 5. Si le backend renvoie un token Firebase, se connecter à Firebase
      if (authResponse.firebaseToken) {
        console.log('🔥 Connexion à Firebase avec le token custom...');
        await signInWithCustomToken(auth, authResponse.firebaseToken);
        console.log('✅ Connexion Firebase réussie');
      } else {
        console.log('⚠️ Pas de token Firebase renvoyé par le backend');
      }
      
      return authResponse;
    } catch (error) {
      console.error('❌ Erreur signInWithBackend:', error);
      throw error;
    }
  }

  // Validation côté client de la signature avant envoi au backend
  static validateSignatureFormat(signature: string, signatureBytesLength: number, walletAddress: string): ClientSideValidationResult {
    const result: ClientSideValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // 1. Vérifier que la signature n'est pas vide
    if (!signature || typeof signature !== 'string') {
      result.errors.push('Signature manquante ou invalide');
      result.isValid = false;
      return result;
    }

    // 2. Vérifier la taille en bytes (doit être 64 pour ED25519)
    if (signatureBytesLength !== 64) {
      result.errors.push(`Taille signature incorrecte: ${signatureBytesLength} bytes (attendu: 64)`);
      result.isValid = false;
    }

    // 3. Vérifier le format base58 (longueur approximative)
    if (signature.length < 80 || signature.length > 90) {
      result.warnings.push(`Longueur signature base58 inhabituelle: ${signature.length} caractères`);
    }

    // 4. Vérifier que la signature contient uniquement des caractères base58 valides
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    if (!base58Regex.test(signature)) {
      result.errors.push('Signature contient des caractères non base58');
      result.isValid = false;
    }

    // 5. Vérifier l'adresse wallet
    const walletRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
    if (!walletRegex.test(walletAddress)) {
      result.errors.push('Format d\'adresse wallet invalide');
      result.isValid = false;
    }

    // 6. Avertissements supplémentaires
    if (signature.startsWith('1111') || signature.includes('0000')) {
      result.warnings.push('Signature suspecte (contient des patterns inhabituels)');
    }

    return result;
  }

  // Test de la connexion backend
  static async testBackendConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${BACKEND_URL}/health`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend connecté:', data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Backend non accessible:', error);
      return false;
    }
  }
}
